import type { InContextDatas } from '@/api/clients/crowdin/crowdin';
import type { TOptions } from 'i18next';
import type { App } from 'vue';

import { useCookies } from '@vueuse/integrations/useCookies';
import i18next from 'i18next';
import BACKEND from 'i18next-http-backend';
import ICU from 'i18next-icu';

import { i18nNamespaces } from '@/config/i18n';

// Setup translation module
export const i18nextInstance = i18next.use(ICU).use(BACKEND);

const langInCookie = useCookies().get('locale');

export const initI18N = async (
  translationUrl: string,
  inContextDatas: InContextDatas | false = false,
) => {
  try {
    // Get path to load resources
    const loadPath = `${translationUrl}/{{lng}}/{{ns}}.json`;
    // Default lang
    let lng = langInCookie?.iso_code_639 ?? 'en-US';

    // If Crowdin is active, set pseudo lang as default lang
    if (typeof inContextDatas === 'object' && inContextDatas.pseudoLang) {
      lng = inContextDatas.pseudoLang;
    }

    // Init 18next
    await i18nextInstance.init({
      ns: i18nNamespaces['global'],
      lng: lng,
      fallbackLng: {
        default: ['en-US'],
      },
      load: 'currentOnly',
      debug: false,
      cache: {
        enabled: true,
        prefix: 'i18next_res_',
        expirationTime: 7 * 24 * 60 * 60 * 1000,
      },
      backend: {
        loadPath,
        parse(data: string) {
          return JSON.parse(data);
        },
      },
      i18nFormat: {
        formats: {
          date: {
            'MM.dd.yyyy': {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric',
            },
            short: {
              month: '2-digit',
              day: '2-digit',
              year: '2-digit',
            },
          },
        },
        parseLngForICU: (lng: string) => {
          return `${lng}-u-ca-gregory-nu-latn`;
        },
      },
    });
  } catch (err) {
    console.error('erreur', err);
  }
};

const GENDERS = {
  m: 'male',
  f: 'female',
  n: 'other',
} as const;

type ShortGender = keyof typeof GENDERS;

function icuGender(shortGender: ShortGender = 'f') {
  return GENDERS[shortGender];
}

// function to format our translation keys into i18n format
export function t(
  key: string,
  options: undefined | TOptions = undefined,
  checkShouldExist = false,
) {
  const newKey = key.replace('.', ':');
  // If needed, check if the key exists in the language files, if not return raw text
  if (checkShouldExist && !i18next.exists(newKey)) {
    return key;
  }

  // If gender passed, format in icu format
  if (options && options.gender) {
    options.gender = icuGender(options.gender as ShortGender);
  }

  const result = i18nextInstance.t(newKey, options as Record<string, unknown>);

  return result;
}

// Add tg function to use gender key
export function tg(
  key: string,
  gender: ShortGender | undefined,
  options: undefined | TOptions = undefined,
) {
  const newKey = `${key}_${gender ?? 'n'}`;
  return t(newKey, options);
}

export default {
  install: (app: App) => {
    app.config.globalProperties.$t = t;
    app.config.globalProperties.$tg = tg;

    app.provide('t', t);
    app.provide('tg', tg);
  },
};
